import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { isValidSignupPhone } from "@/hooks/useAuth";
import { useAuthContext } from "@/hooks/useAuthContext";

type ProfileCompletionModalProps = {
  isOpen: boolean;
};

export const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
}) => {
  const { user, finishRegistrationWithProfile, skipProfileCompletion } =
    useAuthContext();
  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setDisplayName(
      user?.displayName?.trim() ||
        user?.email?.split("@")[0]?.trim() ||
        "",
    );
    setPhoneNumber("");
    setError(null);
  }, [isOpen, user?.displayName, user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = displayName.trim();
    const phone = phoneNumber.trim();
    if (!name) {
      setError("שם מלא שדה חובה");
      return;
    }
    if (phone && !isValidSignupPhone(phone)) {
      setError("מספר טלפון לא תקין");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await finishRegistrationWithProfile({
        displayName: name,
        phoneNumber: phone,
      });
      if (!result.ok) {
        setError(
          result.error === "invalid_phone"
            ? "מספר טלפון לא תקין"
            : result.error === "invalid_name"
              ? "שם מלא שדה חובה"
              : result.error === "phone_save_failed"
                ? "לא הצלחנו לשמור את מספר הטלפון. נסה שוב או דלג."
                : "לא הצלחנו לשמור את הפרופיל. נסה שוב.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md rtl"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-right">השלם את הפרופיל</DialogTitle>
          <DialogDescription className="text-right">
            הזן את שמך ומספר הטלפון. מספר הטלפון אינו חובה — אפשר לדלג.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-full-name" className="text-right block">
              שם מלא <span className="text-red-600">*</span>
            </Label>
            <Input
              id="profile-full-name"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setError(null);
              }}
              required
              className="text-right"
              dir="rtl"
              autoComplete="name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-phone" className="text-right block">
              מספר טלפון{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (אופציונלי)
              </span>
            </Label>
            <Input
              id="profile-phone"
              type="tel"
              inputMode="tel"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                setError(null);
              }}
              className="text-right"
              dir="rtl"
              autoComplete="tel"
              placeholder="מספר הטלפון שלך"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-right bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                  שומר...
                </>
              ) : (
                "המשך"
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={submitting}
              className="w-full"
              onClick={() => skipProfileCompletion()}
            >
              דלג
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
